import React, { useState } from "react";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import { ButtonPrimary, HStack, VStack } from "../../../components/utils";
import { ScheduleParams } from "../../../types/common";
import { useGetAssociatedUsers } from "../../../queries/usersRoles";
import {
  generateCustomReport,
  CustomReportSummary,
} from "../../../api/customReportApi";
import ScheduleSection from "./ScheduleSection";
import { useReportPiiMaskDefault } from "../../../queries/siteSettings";

interface Props {
  report: CustomReportSummary;
  onClose: () => void;
}

const GRAIN_LABEL: Record<string, string> = {
  grant: "Grant",
  employee: "Employee",
  plan: "Plan",
  vesting_tranche: "Vesting Tranche",
  transaction: "Transaction",
};

export function CustomReportGenerateDialog({ report, onClose }: Props) {
  const { maskPiiData: maskPii, setMaskPiiData: setMaskPii } =
    useReportPiiMaskDefault();
  const [scheduleParams, setScheduleParams] = useState<
    ScheduleParams | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);

  const { data: associatedUsers = [] } = useGetAssociatedUsers();

  async function handleSubmit() {
    if (scheduleParams && scheduleParams.recipients.length === 0) {
      toast("Please select at least one recipient to schedule the report", {
        type: "error",
        autoClose: 2000,
      });
      return;
    }
    setLoading(true);
    try {
      const result = await generateCustomReport(report.id, {
        maskPii,
        scheduleParams,
      });
      toast(result.message, { type: "info", autoClose: 4000 });
      onClose();
    } catch {
      toast("Failed to queue the report. Please try again.", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      {/* Header — matches standard ReportDialog */}
      <div className="p-8 text-lg font-medium border-b">
        <h6 className="flex justify-between">
          {report.name}
          <span className="cursor-pointer" onClick={onClose}>
            X
          </span>
        </h6>
      </div>

      <VStack className="w-full gap-2 px-8 py-4">
        {/* Sheet summary */}
        {report.sheets.length > 0 && (
          <VStack className="gap-2 pb-2">
            {report.sheets.map((sheet) => (
              <div
                key={sheet.name}
                className="flex items-start gap-3 bg-tertiaryBg rounded-md px-3 py-2.5"
              >
                <Icon
                  icon="mdi:table-large"
                  width={14}
                  className="text-primary shrink-0 mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-primaryText">
                      {sheet.name}
                    </span>
                    <span className="text-[10px] text-secondaryText capitalize">
                      {GRAIN_LABEL[sheet.grain] ?? sheet.grain} basis
                    </span>
                    <span className="ml-auto text-[10px] text-descriptionColor shrink-0">
                      {sheet.columnCount}{" "}
                      {sheet.columnCount !== 1 ? "columns" : "column"}
                    </span>
                  </div>
                  {sheet.columns.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {sheet.columns.map((col) => (
                        <span
                          key={col}
                          className="inline-block text-[10px] text-primaryText bg-white border border-borderColor rounded px-1.5 py-0.5"
                        >
                          {col}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </VStack>
        )}

        {/* Schedule section */}
        <div className="mt-2 pt-4 border-t border-gray-100">
          <ScheduleSection
            value={scheduleParams}
            onChange={setScheduleParams}
            users={associatedUsers ?? []}
          />
        </div>

        {/* Mask PII checkbox — same as standard dialog */}
        <HStack className="justify-end mt-4 gap-2">
          <input
            type="checkbox"
            checked={maskPii}
            onChange={() => setMaskPii((v) => !v)}
            className="text-white accent-orange-501"
          />
          <span>Hide all Personally Identifiable Information</span>
        </HStack>

        <HStack className="justify-end m-3">
          <p className="italic">
            The report will be queued and available to download once ready.
          </p>
        </HStack>

        <HStack className="justify-end">
          <ButtonPrimary onClick={handleSubmit}>
            {!loading ? (
              scheduleParams ? (
                <span className="flex items-center gap-1.5">
                  <Icon icon="mdi:calendar-clock" width={16} />
                  Generate &amp; Schedule
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <Icon icon="mdi:cog-play-outline" width={16} />
                  Queue Report
                </span>
              )
            ) : (
              <span className="flex items-center gap-1.5">
                <Icon icon="mdi:loading" className="animate-spin" width={16} />
                Queueing...
              </span>
            )}
          </ButtonPrimary>
        </HStack>
      </VStack>
    </div>
  );
}
